'use client'

import { ReactNode, useEffect, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { LoadingIndicator } from '@/components/partials'
import { useMeStore } from '@/store'
import { RoleEnum } from '@/enums'

type PropsType = {
  children: ReactNode
}

const AdminGuard = ({ children }: PropsType) => {
  const router = useRouter()
  const t = useTranslations()
  const { meState, getMe, reset } = useMeStore()
  const { response: user, isLoading, error } = meState
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    // Only call getMe if we don't have user data and are not yet loading
    if (!initialized && !isLoading && !user) {
      getMe().finally(() => setInitialized(true))
    } else if (!initialized && (user || error)) {
      // If we already have data or error, mark as initialized
      setInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, isLoading, user, error])

  useEffect(() => {
    if (!initialized || isLoading) {
      return
    }

    if (!user) {
      // Transient errors (network errors, 5xx) should not force logout.
      // Only redirect for genuine auth failures or missing session.
      if (!error || error.status === 401 || error.status === 403) {
        reset()
        router.push('/login')
      }
      return
    }

    // Check if a user has an ADMIN or ROOT role
    const hasAdminAccess = user.roles?.includes(RoleEnum.ADMIN) || user.roles?.includes(RoleEnum.ROOT)
    if (!hasAdminAccess) {
      const forbiddenError = new Error(t('forbidden_access'))
      forbiddenError.name = 'ForbiddenError'
      throw forbiddenError
    }
  }, [initialized, isLoading, error, user, router, t, reset])

  const loading = !initialized || isLoading
  const hasAdminAccess = user?.roles?.includes(RoleEnum.ROOT) || user?.roles?.includes(RoleEnum.ADMIN)
  const isUnauthorized = error || !user || !hasAdminAccess

  if (loading || isUnauthorized) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <LoadingIndicator />
      </div>
    )
  }

  return <>{children}</>
}

export default AdminGuard
